import { useEffect, useRef } from "react";

const MAX_AMBIENT_PARTICLES = 52;
const BASE_AMBIENT_COUNT = 42;
const BASE_SMOKE_COUNT = 8;
const TRAIL_SAMPLE_COUNT = 120;
const MOBILE_TRAIL_SAMPLE_COUNT = 92;
const REDUCED_TRAIL_SAMPLE_COUNT = 44;
const TRAIL_SPARK_COUNT = 340;
const MOBILE_TRAIL_SPARK_COUNT = 260;
const REDUCED_TRAIL_SPARK_COUNT = 58;
const TRAIL_MICRO_SPARK_COUNT = 1078;
const MOBILE_TRAIL_MICRO_SPARK_COUNT = 792;
const REDUCED_TRAIL_MICRO_SPARK_COUNT = 132;
const TRAIL_ORBS = [
  { t: 0.13, radius: 3.8, glow: 16 },
  { t: 0.36, radius: 4.6, glow: 20 },
  { t: 0.58, radius: 3.2, glow: 14 },
  { t: 0.82, radius: 4.2, glow: 18 },
];

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function spawnParticle(width, height, kind = "dust") {
  if (kind === "smoke") {
    return {
      kind: "smoke",
      x: rand(0, width),
      y: rand(0, height),
      vx: rand(-0.18, 0.18) * 0.4,
      vy: rand(-0.15, 0.08) * 0.4,
      radius: rand(4, 12),
      wobbleFreq: rand(0.0003, 0.0008),
      wobbleAmp: rand(0.04, 0.12),
      life: 0,
      maxLife: rand(9000, 16000),
      baseHue: rand(38, 50),
      saturation: rand(30, 45),
      peakAlpha: rand(0.03, 0.08),
    };
  }
  return {
    kind: "dust",
    x: rand(0, width),
    y: rand(0, height),
    vx: rand(-0.18, 0.18),
    vy: rand(-0.15, 0.08),
    radius: rand(0.3, 0.85),
    wobbleFreq: rand(0.0008, 0.002),
    wobbleAmp: rand(0.08, 0.22),
    life: 0,
    maxLife: rand(5500, 11000),
    baseHue: rand(40, 50),
  };
}

function makeTrailPoints(width, height, sampleCount) {
  const points = [];
  const scale = Math.min(width, height) / 402;
  const amplitude = Math.min(width * 0.34, 150 * scale);
  const center = width * (width < 520 ? 0.5 : 0.57);
  const top = -height * 0.07;
  const span = height * 1.15;

  for (let i = 0; i < sampleCount; i++) {
    const t = sampleCount === 1 ? 0 : i / (sampleCount - 1);
    const y = top + span * t;
    const wave = Math.sin(t * Math.PI * 3.15 - 0.72);
    const secondary = Math.sin(t * Math.PI * 6.2 + 0.95) * 0.18;
    const taper = 0.78 + 0.22 * Math.sin(t * Math.PI);
    const x = center + (wave + secondary) * amplitude * taper;
    points.push({ x, y, t });
  }

  return points;
}

function getTrailPoint(points, t) {
  if (!points.length) return { x: 0, y: 0, t: 0 };
  const clamped = Math.max(0, Math.min(1, t));
  const index = clamped * (points.length - 1);
  const low = Math.floor(index);
  const high = Math.min(points.length - 1, low + 1);
  const mix = index - low;
  const a = points[low];
  const b = points[high];
  return {
    x: a.x + (b.x - a.x) * mix,
    y: a.y + (b.y - a.y) * mix,
    t: clamped,
  };
}

function spawnTrailSpark(width) {
  const t = Math.random();
  const hotBand = 0.45 + 0.55 * Math.sin(t * Math.PI);
  const scatter = rand(-24, 24) + rand(-28, 28) * hotBand;

  return {
    normalOffset: scatter,
    tangentOffset: rand(-9, 9),
    t,
    along: rand(-0.00012, 0.00024),
    drift: rand(-0.07, 0.07),
    radius: rand(0.28, 0.95) * (width < 520 ? 0.9 : 1),
    life: rand(0, 1),
    speed: rand(0.000045, 0.00012),
    pulse: rand(0.006, 0.015),
    warmth: rand(34, 41),
  };
}

function spawnMicroSpark() {
  return {
    t: Math.random(),
    normalOffset: rand(-52, 52),
    tangentOffset: rand(-24, 24),
    radius: rand(0.16, 0.66),
    opacity: rand(0.2, 0.78),
    drift: rand(-0.00008, 0.00016),
    pulse: rand(0.004, 0.014),
    warmth: rand(33, 42),
  };
}

export default function AtmosphereCanvas() {
  const particleRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const particleCanvas = particleRef.current;
    const container = containerRef.current;
    if (!particleCanvas || !container) return;

    const particleCtx = particleCanvas.getContext("2d");

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const isMobile =
      typeof window !== "undefined" &&
      (window.matchMedia("(max-width: 767px)").matches ||
        /Mobi|Android/i.test(navigator.userAgent));

    const lowPowerDevice =
      typeof navigator !== "undefined" &&
      (navigator.hardwareConcurrency || 8) <= 4;

    const targetFps = reducedMotion ? 20 : isMobile ? 45 : 60;
    const frameInterval = 1000 / targetFps;
    const speedScale = reducedMotion ? 0.16 : 0.4;
    const ambientCount = reducedMotion
      ? 14
      : Math.min(lowPowerDevice ? 28 : BASE_AMBIENT_COUNT, MAX_AMBIENT_PARTICLES);
    const smokeCount = reducedMotion ? 3 : lowPowerDevice ? 5 : BASE_SMOKE_COUNT;
    const trailSampleCount = reducedMotion
      ? REDUCED_TRAIL_SAMPLE_COUNT
      : isMobile || lowPowerDevice
        ? MOBILE_TRAIL_SAMPLE_COUNT
        : TRAIL_SAMPLE_COUNT;
    const trailSparkCount = reducedMotion
      ? REDUCED_TRAIL_SPARK_COUNT
      : isMobile || lowPowerDevice
        ? MOBILE_TRAIL_SPARK_COUNT
        : TRAIL_SPARK_COUNT;
    const microSparkCount = reducedMotion
      ? REDUCED_TRAIL_MICRO_SPARK_COUNT
      : isMobile || lowPowerDevice
        ? MOBILE_TRAIL_MICRO_SPARK_COUNT
        : TRAIL_MICRO_SPARK_COUNT;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let trailPoints = [];
    let trailSparks = [];
    let microSparks = [];

    const seedTrail = () => {
      trailPoints = makeTrailPoints(width, height, trailSampleCount);
      trailSparks = [];
      for (let i = 0; i < trailSparkCount; i++) {
        trailSparks.push(spawnTrailSpark(width));
      }
      microSparks = [];
      for (let i = 0; i < microSparkCount; i++) {
        microSparks.push(spawnMicroSpark());
      }
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      particleCanvas.width = Math.floor(width * dpr);
      particleCanvas.height = Math.floor(height * dpr);
      particleCanvas.style.width = width + "px";
      particleCanvas.style.height = height + "px";
      particleCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (trailPoints.length) seedTrail();
    };

    resize();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(container);
    } else {
      window.addEventListener("resize", resize);
    }

    const particles = [];
    seedTrail();

    for (let i = 0; i < ambientCount; i++) {
      const p = spawnParticle(width, height, "dust");
      p.life = rand(0, p.maxLife);
      particles.push(p);
    }
    for (let i = 0; i < smokeCount; i++) {
      const p = spawnParticle(width, height, "smoke");
      p.life = rand(0, p.maxLife);
      particles.push(p);
    }

    let rafId = 0;
    let lastFrame = 0;

    const drawTrail = (dt) => {
      if (!trailPoints.length) return;

      const time = performance.now() * speedScale;
      particleCtx.save();
      particleCtx.globalCompositeOperation = "screen";
      particleCtx.lineCap = "round";
      particleCtx.lineJoin = "round";

      const glowRadius = isMobile ? 41 : 55;
      const glowStep = reducedMotion ? 3 : 2;
      for (let i = 0; i < trailPoints.length; i += glowStep) {
        const point = trailPoints[i];
        const shimmer = Math.sin(time * 0.0013 + point.t * 18) * 1.2;
        const x = point.x + shimmer;
        const y = point.y;
        const waist = 0.72 + 0.28 * Math.sin(point.t * Math.PI);
        const radius = glowRadius * waist;
        const centerAlpha = (reducedMotion ? 0.042 : 0.068) * waist;
        const glow = particleCtx.createRadialGradient(x, y, 0, x, y, radius);
        glow.addColorStop(0, `hsla(39, 100%, 72%, ${centerAlpha * 1.1})`);
        glow.addColorStop(0.34, `hsla(38, 98%, 61%, ${centerAlpha * 0.42})`);
        glow.addColorStop(0.68, `hsla(36, 94%, 51%, ${centerAlpha * 0.16})`);
        glow.addColorStop(1, "hsla(35, 94%, 50%, 0)");
        particleCtx.fillStyle = glow;
        particleCtx.beginPath();
        particleCtx.arc(x, y, radius, 0, Math.PI * 2);
        particleCtx.fill();
      }

      for (let i = 0; i < TRAIL_ORBS.length; i++) {
        const orb = TRAIL_ORBS[i];
        const p = getTrailPoint(trailPoints, (orb.t + time * 0.000035) % 1);
        const pulse = 0.58 + 0.18 * Math.sin(time * 0.003 + i * 1.7);
        const grad = particleCtx.createRadialGradient(p.x, p.y, 0, p.x, p.y, orb.glow * pulse);
        grad.addColorStop(0, `hsla(40, 100%, 82%, ${0.16 * pulse})`);
        grad.addColorStop(0.28, `hsla(36, 100%, 63%, ${0.08 * pulse})`);
        grad.addColorStop(1, "hsla(35, 100%, 58%, 0)");
        particleCtx.fillStyle = grad;
        particleCtx.beginPath();
        particleCtx.arc(p.x, p.y, orb.glow * pulse, 0, Math.PI * 2);
        particleCtx.fill();
        particleCtx.fillStyle = `hsla(43, 100%, 86%, ${0.28 * pulse})`;
        particleCtx.beginPath();
        particleCtx.arc(p.x, p.y, orb.radius * pulse, 0, Math.PI * 2);
        particleCtx.fill();
      }

      for (let i = 0; i < microSparks.length; i++) {
        const spark = microSparks[i];
        spark.t += spark.drift * dt * speedScale;
        if (spark.t < 0 || spark.t > 1) {
          microSparks[i] = spawnMicroSpark();
          continue;
        }

        const point = getTrailPoint(trailPoints, spark.t);
        const next = getTrailPoint(trailPoints, Math.min(1, spark.t + 0.01));
        const dx = next.x - point.x;
        const dy = next.y - point.y;
        const len = Math.hypot(dx, dy) || 1;
        const tangentX = dx / len;
        const tangentY = dy / len;
        const normalX = -dy / len;
        const normalY = dx / len;
        const density = 0.3 + 0.7 * Math.sin(spark.t * Math.PI);
        const shimmer = 0.65 + 0.35 * Math.sin(time * spark.pulse + i);
        const x =
          point.x +
          normalX * spark.normalOffset * density +
          tangentX * spark.tangentOffset;
        const y =
          point.y +
          normalY * spark.normalOffset * density +
          tangentY * spark.tangentOffset;
        const opacity = spark.opacity * shimmer * (reducedMotion ? 0.51 : 0.92);

        particleCtx.fillStyle = `hsla(${spark.warmth}, 100%, 73%, ${opacity})`;
        particleCtx.beginPath();
        particleCtx.arc(x, y, spark.radius, 0, Math.PI * 2);
        particleCtx.fill();
      }

      for (let i = 0; i < trailSparks.length; i++) {
        const spark = trailSparks[i];
        spark.life += dt * spark.speed * speedScale;
        spark.t += dt * spark.along * speedScale;

        if (spark.life >= 1 || spark.t < 0 || spark.t > 1) {
          trailSparks[i] = spawnTrailSpark(width);
          continue;
        }

        const point = getTrailPoint(trailPoints, spark.t);
        const next = getTrailPoint(trailPoints, Math.min(1, spark.t + 0.012));
        const dx = next.x - point.x;
        const dy = next.y - point.y;
        const len = Math.hypot(dx, dy) || 1;
        const tangentX = dx / len;
        const tangentY = dy / len;
        const normalX = -dy / len;
        const normalY = dx / len;
        const spread = Math.sin(spark.t * Math.PI) * (isMobile ? 18 : 26);
        const flutter = Math.sin(time * spark.pulse + i) * spread;
        const normalOffset = spark.normalOffset + flutter;
        const tangentOffset = spark.tangentOffset + Math.cos(time * spark.pulse + i) * 5;
        const x =
          point.x +
          normalX * normalOffset +
          tangentX * tangentOffset +
          spark.drift * dt * 0.02;
        const y = point.y + normalY * normalOffset + tangentY * tangentOffset;
        const fade = Math.min(spark.life / 0.18, (1 - spark.life) / 0.28, 1);
        const twinkle = 0.62 + 0.38 * Math.sin(time * 0.007 + i * 1.91);
        const opacity = Math.max(0, fade) * twinkle;
        const core = spark.radius * (0.8 + twinkle * 0.5);

        if (opacity <= 0.02) continue;

        const glow = particleCtx.createRadialGradient(x, y, 0, x, y, core * 4.8);
        glow.addColorStop(0, `hsla(${spark.warmth}, 100%, 76%, ${opacity * 0.2})`);
        glow.addColorStop(1, `hsla(${spark.warmth}, 100%, 62%, 0)`);
        particleCtx.fillStyle = glow;
        particleCtx.beginPath();
        particleCtx.arc(x, y, core * 5.2, 0, Math.PI * 2);
        particleCtx.fill();

        particleCtx.fillStyle = `hsla(${spark.warmth + 3}, 100%, 84%, ${opacity * 0.35})`;
        particleCtx.beginPath();
        particleCtx.arc(x, y, core, 0, Math.PI * 2);
        particleCtx.fill();
      }

      particleCtx.restore();
    };

    const drawParticles = (dt) => {
      particleCtx.clearRect(0, 0, width, height);
      drawTrail(dt);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.life += dt;

        if (p.life >= p.maxLife) {
          particles[i] = spawnParticle(width, height, p.kind);
          continue;
        }

        const isSmoke = p.kind === "smoke";

        if (isSmoke) {
          p.vx += (Math.random() - 0.5) * 0.009;
          p.vy += (Math.random() - 0.5) * 0.006;
          p.vx = Math.max(-0.1, Math.min(0.1, p.vx));
          p.vy = Math.max(-0.08, Math.min(0.06, p.vy));
        } else {
          p.vx += (Math.random() - 0.5) * 0.022;
          p.vy += (Math.random() - 0.5) * 0.015;
          p.vx = Math.max(-0.25, Math.min(0.25, p.vx));
          p.vy = Math.max(-0.2, Math.min(0.15, p.vy));
        }

        const wobble = Math.sin(p.life * p.wobbleFreq) * p.wobbleAmp;
        p.x += (p.vx + wobble) * dt * 0.06 * speedScale;
        p.y += p.vy * dt * 0.06 * speedScale;

        const edgeBuffer = isSmoke ? p.radius + 4 : 20;
        if (p.x < -edgeBuffer) p.x = width + edgeBuffer;
        if (p.x > width + edgeBuffer) p.x = -edgeBuffer;
        if (p.y < -edgeBuffer) p.y = height + edgeBuffer;
        if (p.y > height + edgeBuffer) p.y = -edgeBuffer;

        const lifeFrac = p.life / p.maxLife;

        if (isSmoke) {
          const fadeIn = Math.min(1, lifeFrac / 0.25);
          const fadeOut = Math.min(1, (1 - lifeFrac) / 0.35);
          const opacity = Math.min(fadeIn, fadeOut) * p.peakAlpha;
          if (opacity <= 0.003) continue;

          const grad = particleCtx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
          grad.addColorStop(0, `hsla(${p.baseHue}, ${p.saturation}%, 68%, ${opacity})`);
          grad.addColorStop(0.5, `hsla(${p.baseHue}, ${p.saturation}%, 66%, ${opacity * 0.45})`);
          grad.addColorStop(1, `hsla(${p.baseHue}, ${p.saturation}%, 64%, 0)`);
          particleCtx.fillStyle = grad;
          particleCtx.beginPath();
          particleCtx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          particleCtx.fill();
          continue;
        }

        const fadeIn = Math.min(1, lifeFrac / 0.2);
        const fadeOut = Math.min(1, (1 - lifeFrac) / 0.3);
        const twinkle = 0.75 + 0.25 * Math.sin(p.life * 0.004 + p.x * 0.02);
        const opacity = Math.min(fadeIn, fadeOut) * 0.85 * twinkle;

        if (opacity <= 0.01) continue;

        const r = p.radius;
        const haloR = r * 2.2;
        const halo = particleCtx.createRadialGradient(p.x, p.y, 0, p.x, p.y, haloR);
        halo.addColorStop(0, `hsla(${p.baseHue}, 90%, 72%, ${opacity * 0.45})`);
        halo.addColorStop(1, `hsla(${p.baseHue}, 90%, 70%, 0)`);
        particleCtx.fillStyle = halo;
        particleCtx.beginPath();
        particleCtx.arc(p.x, p.y, haloR, 0, Math.PI * 2);
        particleCtx.fill();

        particleCtx.fillStyle = `hsla(${p.baseHue + 4}, 95%, 82%, ${opacity})`;
        particleCtx.beginPath();
        particleCtx.arc(p.x, p.y, r, 0, Math.PI * 2);
        particleCtx.fill();
      }
    };

    const loop = (now) => {
      rafId = requestAnimationFrame(loop);
      const elapsed = now - lastFrame;
      if (elapsed < frameInterval) return;
      const dt = lastFrame === 0 ? frameInterval : elapsed;
      lastFrame = now;
      drawParticles(dt);
    };

    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <canvas
        ref={particleRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          mixBlendMode: "screen",
        }}
      />
    </div>
  );
}
